<template>
	<div>
		<DetailsLayout
			v-if="!fetchChatError"
			:groupIconImage="group.icon_image_url"
			:groupIconEmoji="group.icon_emoji"
			:superTitle="group.category ? group.category.title : ''"
			:title="group.title"
			:label="$t('admin_groups_group_members_title')"
			:buttonText="$t('admin_groups_add_members_action')"
			:buttonProps="{ disabled: !canAddMembers }"
			:placeholder="searchPlaceholder"
			:defaultSearch="query"
			:loading="fetchingChatDetails"
			class="group-details"
			@click="showAddMembers = true"
			@search="onSearch"
		>
			<template #table>
				<Table
					:columns="columns"
					:rows="memberships"
					clickable
					infinite
					localize
					closePopoversOnScroll
					:loadingOverlay="fetchingMemberships"
					:defaultSort="sort"
					:error="fetchMembershipsError"
					headerSize="small"
					@row-click="goToUserDetails"
					@sort="onSort"
					@retry="fetchMemberships"
					height="calc(100vh - 296px)"
					ref="table"
					@load-more="loadMore"
					:loadingMore="loadingMore"
				>
					<template #cell-latest_user_message_created_at="{ value }">
						{{
							value
								? timeFromNow(value)
								: $t('groupsettings_option_category_uncatagorized')
						}}
					</template>
					<template #cell-user="{ value }">
						<UserCell :user="value" />
					</template>
					<template #cell-role="{ data }">
						<div v-if="data.user.role === 'admin'" class="group-role-text-only">
							{{ $t(`model_role_${data.role.toLowerCase()}`) }}
						</div>

						<div v-if="data.user.role !== 'admin'" @click.stop>
							<Dropdown
								:options="dropdownOptions"
								v-model="data.role"
								global
								small
								class="role-dropdown"
								optionsClass="group-details-role"
								@change="updateRole(data, $event)"
							>
								<template #selected="{ option }">
									{{ option.label }}
								</template>
								<template v-slot="{ option }">
									<div class="role-title" v-text="option.label" />
									<div class="role-desc" v-text="option.desc" />
								</template>
							</Dropdown>
						</div>
					</template>
					<template #cell-actions="{ data }">
						<DotsMenu
							localize
							:items="menuOptions"
							@click.stop
							@select="onMenuItemClick($event, data)"
						/>
					</template>
					<template #empty>
						<EmptyState
							v-if="!query"
							:icon="GroupsIcon"
							:text="$t('admin_groups_empty_group_label')"
							:buttonText="$t('admin_groups_add_members_action')"
							:buttonProps="{ disabled: !canAddMembers }"
							@click="showAddMembers = true"
						/>
						<NoSearchResults v-if="query" :query="query" />
					</template>
				</Table>
			</template>
			<template #sidebar>
				<SidebarSection>
					<InfoList
						localize
						:items="infoItems"
						title="admin_groups_group_info_label"
					/>
				</SidebarSection>
				<SidebarSection>
					<ActionList
						localize
						:items="actionItems"
						title="admin_groups_actions_label"
						@select="onSelectAction"
					/>
				</SidebarSection>
				<SidebarSection v-if="canChangeSuper">
					<div class="super-group-container">
						<div class="super-group-header">
							<div class="super-group-label">
								<span v-t="'chat_super_title'" />
								<span class="super-group-tag" v-t="'common_beta'" />
							</div>
							<ToggleSwitch
								v-model="group.is_supergroup"
								:loading="isSavingSuper"
								:disabled="!canDisableSuper"
								@click="onClickSuper"
								@change="onChangeSuper"
							/>
						</div>
						<div
							class="super-group-setting"
							v-text="
								$t('chat_setting_member_limit_auto', {
									Count: org.bubble_membership_cap,
								})
							"
						/>
						<div class="super-group-desc" v-t="'chat_super_description'" />
					</div>
				</SidebarSection>
				<SidebarSection v-if="canChangeSuper">
					<ActionList @select="onSelectAction" localize :items="extraItems" />
				</SidebarSection>
				<SidebarSection v-if="group && group.locked">
					<ActionList localize :items="lockedItems" />
				</SidebarSection>
			</template>
			<template #header-icon>
				<GroupIcon :imageUrl="group.icon_image_url" :emoji="group.icon_emoji" />
			</template>
		</DetailsLayout>

		<DeleteGroupModal
			v-if="deleteGroup"
			:group="group"
			@close="deleteGroup = false"
			@delete="exitDetail"
		/>

		<PermissionsModal
			v-if="group.id && showPermissions"
			:group="group"
			@success="group = $event"
			@close="showPermissions = false"
		/>

		<NewGroupModal
			v-if="showGroupModal"
			:group="group"
			@success="onNewGroupSuccess"
			@close="showGroupModal = false"
		/>

		<AddMembersModal
			v-if="showAddMembers"
			:group="group"
			@cancel="showAddMembers = false"
			@success="onAddMembersSuccess"
		/>

		<RemoveFromGroup
			v-if="showRemoveFromGroup"
			@cancel="showRemoveFromGroup = false"
			@success="removeUser"
			:group="group"
			:person="removeFromGroupData.user"
		/>

		<FullPageError @retry="fetchRetry" v-if="fetchChatError" />

		<StandardModal
			v-if="showSuperDisableModal"
			open
			small
			:title="$t('chat_super_title')"
			:confirmText="$t('common_button_close')"
			:confirmProps="{ danger: true }"
			hideCancel
			@confirm="showSuperDisableModal = false"
			@cancel="showSuperDisableModal = false"
		>
			<div class="super-error-modal-wrapper">
				<CircleExclamationIcon halo />
				{{
					$t('chat_setting_member_limit_error', {
						Count: org.bubble_membership_cap,
					})
				}}
			</div>
		</StandardModal>
	</div>
</template>

<script>
import store, { getOrganization, getMyUser } from '@/store'
import { ACTIVITY_LOG, orgFlag } from '@/helpers/flags.js'
import {
	Table,
	Dropdown,
	DotsMenu,
	ASC,
	ToggleSwitch,
	StandardModal,
	GroupIcon,
} from '@/components/common'
import {
	GroupsIcon,
	PencilIcon,
	TrashCanIcon,
	LockKeyholeIcon,
	CircleExclamationIcon,
} from '@/icons'
import {
	InfoList,
	ActionList,
	DetailsLayout,
	SidebarSection,
	get,
	UserCell,
	NoSearchResults,
	EmptyState,
	FullPageError,
	post,
	patch,
} from '../common'

import RemoveFromGroup from '../People/RemoveFromGroup'
import DeleteGroupModal from './DeleteGroupModal'
import { getTimeAgoShorthand, canRetryAPIError } from '@/helpers/utils'
import { formatNumber } from '@/i18n'
import PermissionsModal from './PermissionsModal'
import NewGroupModal from './NewGroupModal'
import AddMembersModal from './AddMembersModal'
import debounce from 'debounce'
import { closeAllMenus } from '../../common/Menu.vue'
import { fetchMyMembershipForChat } from '@/api/chat'

const PAGE_SIZE = 30

export default {
	name: 'GroupDetails',
	components: {
		Table,
		InfoList,
		ActionList,
		DotsMenu,
		Dropdown,
		DetailsLayout,
		SidebarSection,
		UserCell,
		DeleteGroupModal,
		PermissionsModal,
		NewGroupModal,
		AddMembersModal,
		NoSearchResults,
		EmptyState,
		FullPageError,
		RemoveFromGroup,
		ToggleSwitch,
		StandardModal,
		CircleExclamationIcon,
		GroupIcon,
	},
	props: {
		groupId: [Number, String],
		cachedGroup: Object,
	},
	data() {
		return {
			category: {},
			group: this.cachedGroup || {},
			org: getOrganization(),
			GroupsIcon,
			memberships: [],
			query: null,
			fetchChatError: false,
			fetchMembershipsError: false,
			fetchingMemberships: true,
			fetchingChatDetails: true,
			removeFromGroupData: null,
			showPermissions: false,
			showGroupModal: false,
			showRemoveFromGroup: false,
			deleteGroup: false,
			showAddMembers: false,
			loadingMore: false,
			isSavingSuper: false,
			initialSuperState: null,
			showSuperDisableModal: false,
			sort: {
				field: 'user',
				order: ASC,
				param: 'user.firstname,user.lastname',
			},
			columns: [
				{
					field: 'user',
					label: 'admin_people_name_label',
					sorter: true,
					ascSort: 'user.firstname,user.lastname',
					dscSort: '-user.firstname,-user.lastname',
				},
				{
					field: 'role',
					label: 'people_group_role_button',
					width: '400px',
					sorter: true,
					ascSort: 'role',
					dscSort: '-role',
				},
				{
					field: 'latest_user_message_created_at',
					label: 'admin_groups_last_message_label',
					width: '300px',
					sorter: true,
					ascSort: 'latest_user_message_created_at',
					dscSort: '-latest_user_message_created_at',
				},
				{
					field: 'actions',
					align: 'right',
					width: '100px',
				},
			],
			isAdminMemberOfGroup: false,
		}
	},

	created() {
		this.fetchMembershipsDebounced = debounce(
			this.fetchMemberships.bind(this),
			500
		)
	},

	async mounted() {
		this.checkQueryParams()
		this.fetchChatDetails()
		this.fetchMemberships()
		this.checkAdminMembership()
	},

	computed: {
		menuOptions() {
			const items = [
				{
					id: 'details',
					label: 'admin_groups_open_person_details_action',
				},
				{
					id: 'remove',
					label: 'people_removefromgroup_button',
					danger: true,
				},
			]
			if (this.isAdminMemberOfGroup && orgFlag(ACTIVITY_LOG)) {
				items.push({
					id: 'activity',
					label: 'admin_activity_view_activity_action',
				})
			}
			return items
		},

		actionItems() {
			const items = [
				{
					id: 'edit',
					icon: PencilIcon,
					label: 'admin_groups_edit_group_button',
				},
				{
					id: 'permissions',
					icon: LockKeyholeIcon,
					label: 'admin_groups_manage_permissions_button',
				},
			]
			if (!this.canChangeSuper) {
				items.push(...this.extraItems)
			}
			return items
		},

		extraItems() {
			const cannotDelete = this.group.deletegroup === 'system'
			return [
				{
					id: 'delete',
					icon: TrashCanIcon,
					type: 'danger',
					label: 'admin_groups_delete_group_action',
					disabled: cannotDelete,
				},
			]
		},

		infoItems() {
			return [
				{
					label: 'admin_groups_group_id_label',
					value: this.group.id || this.groupId,
				},
				{
					label: 'admin_groups_created_on_label',
					value: this.group.id && this.$m(this.group.created_at).format('L'),
				},
				{
					label: 'admin_groups_members_label',
					value: this.group?.memberships_count,
				},
				{
					label: 'admin_groups_message_sent_label',
					value: this.group.latest_user_message_created_at
						? this.$m(this.group.latest_user_message_created_at).format('L')
						: this.$t('admin_common_no_result'),
				},
				{
					label: 'admin_groups_messages_count_label',
					value: this.group.id && formatNumber(this.group.messages_count),
				},
			]
		},

		lockedItems() {
			return [
				{
					id: 'locked',
					icon: LockKeyholeIcon,
					type: 'primary',
					label: 'admin_common_locked_record_label',
					noClick: true,
				},
			]
		},

		canChangeSuper() {
			const me = getMyUser()
			return me.role === 'support'
		},

		canDisableSuper() {
			return this.org.bubble_membership_cap > this.group.memberships_count
		},

		searchPlaceholder() {
			return this.$t('admin_groups_search_members_label', {
				GroupName: this.group.title,
			})
		},

		dropdownOptions() {
			return [
				{
					id: 'owner',
					label: this.$t('model_role_owner'),
					desc: this.$t('people_role_owner_explanation'),
				},
				{
					id: 'member',
					label: this.$t('model_role_member'),
					desc: this.$t('people_role_member_explanation'),
				},
			]
		},

		canAddMembers() {
			return this.group.memberships_available > 0 && !this.group.locked
		},
	},
	methods: {
		onNewGroupSuccess(group) {
			this.group = group
			this.showGroupModal = false
			if (this.group.icon_image_url) {
				this.group.icon_image_url = store.mediaPath({
					url: this.group.icon_image_url,
				})
			}
		},
		onAddMembersSuccess() {
			this.fetchMemberships()
			this.fetchChatDetails()
		},
		onSearch(query) {
			this.query = query || null
			this.fetchingMemberships = true
			this.fetchMembershipsDebounced()
		},

		timeFromNow(dateStr) {
			const time = getTimeAgoShorthand(dateStr)
			return this.$t('common_date_relative', [time])
		},

		onClickSuper() {
			// if group can be un-super-ified, show error modal w/ details
			if (this.group.is_supergroup && !this.canDisableSuper) {
				this.showSuperDisableModal = true
			}
		},

		onChangeSuper(is_supergroup) {
			if (this.initialSuperState === null) {
				this.initialSuperState = is_supergroup
				return
			}
			this.initialSuperState = is_supergroup
			if (!is_supergroup && !this.canDisableSuper) return
			this.saveSuperGroup()
		},

		onMenuItemClick({ id }, chat) {
			switch (id) {
				case 'remove':
					this.showRemoveFromGroup = true
					this.removeFromGroupData = chat
					break
				case 'details':
					this.goToUserDetails(chat)
					break
				case 'activity':
					this.$router.push({
						name: 'group-user-activity',
						params: { chatId: this.groupId, userId: chat.user_id },
					})
					break
			}
		},

		onSelectAction({ id }) {
			switch (id) {
				case 'edit':
					this.showGroupModal = true
					break
				case 'permissions':
					this.showPermissions = true
					break
				case 'delete':
					this.deleteGroup = true
					break
			}
		},

		goToUserDetails(chat) {
			closeAllMenus()
			this.$router.push({
				name: 'user',
				params: { id: chat.user_id },
			})
		},

		async updateRole(membership, role) {
			try {
				await post(
					`/organization/groups/${this.groupId}/memberships/${membership.user_id}`,
					{ role }
				)
				this.$alert.success(
					this.$t('admin_people_changes_saved_confirmation_label')
				)
			} catch (e) {
				//TODO: We should probably set the role back to what it was if it fails?  But how do we know what it was?
				this.$alert.error(this.$t('common_api_error_save'))
			}
		},

		async removeUser() {
			this.memberships = this.memberships.filter(
				(m) => m.user_id !== this.removeFromGroupData.user_id
			)
			this.removeFromGroupData = null
			this.fetchChatDetails()
		},

		onSort(sort, { ascSort, dscSort }) {
			this.sort = {
				...sort,
				param: sort.order === ASC ? ascSort : dscSort,
			}
			this.fetchMemberships()
		},

		checkQueryParams() {
			const { add, perms, q, ...others } = this.$route.query
			if (add) this.showAddMembers = true
			if (perms) this.showPermissions = true
			if (q) this.query = q
			if (add || perms || q) {
				this.$router.replace({ query: others })
			}
		},

		fetchRetry() {
			this.fetchChatDetails()
			this.fetchMemberships()
		},

		async checkAdminMembership() {
			try {
				await fetchMyMembershipForChat(this.groupId)
				this.isAdminMemberOfGroup = true
			} catch (e) {
				console.error(e)
				this.isAdminMemberOfGroup = false
			}
		},

		async fetchChatDetails() {
			this.fetchChatError = false
			this.fetchingChatDetails = true
			try {
				const apiResponse = await get(`/organization/groups/${this.groupId}`)
				this.group = apiResponse.data
				if (this.group?.icon_image_url) {
					this.group.icon_image_url = store.mediaPath({
						url: this.group.icon_image_url,
					})
				}
				this.fetchingChatDetails = false
			} catch (e) {
				this.fetchingChatDetails = false
				this.$alert.error(this.$t('common_api_error_save'))
				if (canRetryAPIError(e)) {
					// if a retry is possible, show fullpage error
					this.fetchChatError = true
				} else {
					// otherwise route to groups list page
					this.$router.replace({ name: 'groups' })
				}
			}
		},

		async fetchMemberships() {
			this.fetchMembershipsError = false
			this.fetchingMemberships = true
			const { param: sort } = this.sort
			const params = { 'page[size]': PAGE_SIZE, sort }
			if (this.query) {
				params['filter[search]'] = this.query
			}
			try {
				this.apiResponse = await get(
					`/organization/groups/${this.groupId}/memberships`,
					params
				)
				this.memberships = this.apiResponse.data
				this.fetchingMemberships = false
				this.$refs.table.scrollToTop()
			} catch (e) {
				console.error(e)
				this.fetchMembershipsError = true
				this.fetchingMemberships = false
				this.$alert.error(this.$t('common_api_error_save'))
			}
		},

		async loadMore() {
			if (!this.apiResponse?.next) return
			this.loadingMore = true
			try {
				this.apiResponse = await this.apiResponse.next()
				this.memberships = this.memberships.concat(this.apiResponse.data)
				this.loadingMore = false
			} catch (e) {
				console.error(e)
				this.loadingMore = false
				this.$alert.error(this.$t('common_api_error_save'))
			}
		},

		exitDetail() {
			closeAllMenus()
			this.$router.push('/organization/groups')
		},

		async saveSuperGroup() {
			this.isSavingSuper = true
			try {
				await patch(`/organization/groups/${this.group.id}`, {
					is_supergroup: this.group.is_supergroup,
				})
				this.$alert.success(
					this.$t('admin_people_changes_saved_confirmation_label')
				)
			} catch (e) {
				console.error(e)
				this.$alert.error(this.$t('common_api_error_save'))
			}
			this.isSavingSuper = false
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.group-details {
	.role-dropdown {
		width: 134px;
	}
}

.super-group-container {
	color: var(--neutral-800);
	font-family: 'Avenir-Book';
	.super-group-header {
		@include flex-spread;
		.super-group-label {
			@include flex(8px);
			font-size: 12px;
			.super-group-tag {
				padding: 2px 4px;
				font-size: 10px;
				background: var(--neutral-200);
				border-radius: 4px;
			}
		}
		.pronto-toggle {
			font-size: 27px;
		}
	}
	.super-group-setting,
	.super-group-desc {
		font-size: 12px;
		color: var(--neutral-600);
		white-space: pre-wrap;
		margin: 4px 0;
	}
	.super-group-setting {
		font-style: italic;
	}
}

.super-error-modal-wrapper {
	@include flex-column-center;
	max-width: 300px;
	margin: 24px auto;
	text-align: center;
	.pronto-icon {
		color: var(--red-200);
		font-size: 50px;
		margin-bottom: 24px;
	}
}
</style>

<style lang="scss">
@import 'src/sass/variables';

.group-details-role {
	.dropdown-option {
		display: flex !important;
		flex-direction: column;
		.role-title {
			color: var(--neutral-800);
		}
		.role-desc {
			color: var(--neutral-400);
			font-size: 12px;
		}
	}
}

.group-role-text-only {
	text-transform: capitalize;
	margin-left: 14px;
}
</style>
